import { Component, inject, signal } from "@angular/core";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from "@angular/material/dialog";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { interval } from "rxjs";
import { HeaderDialogComponent } from "../../modules/dialog/header-dialog/header-dialog.component";
import { BigButtonComponent } from "../big-button/big-button.component";
import { UsageDetailComponent } from "../usage-detail/usage-detail.component";
import { parseNumber } from "../../utils/number-utils.service";
import { TranslatePipe } from "../../pipes/translate.pipe";
import { Router } from "@angular/router";

/**
 * Une ligne de la ventilation dépliable : « de quoi » est fait le compte annoncé par le message.
 *
 * ⚠️ **Le libellé est déjà TRADUIT par l'appelant.** Le dialogue est générique : il ne peut pas savoir
 * que `article-infos` se dit « versions de fiche article », et une clef de traduction résolue ici
 * obligerait chaque appelant à respecter une convention de nommage que rien ne vérifierait.
 */
export interface ConfirmationDetail {
  label: string;
  count: number;
  /**
   * Les éléments NOMMÉS de ce groupe, déjà mis en forme par l'appelant.
   *
   * ⚠️ **Le compte seul ne suffit pas** : « 4 fiches article » ne dit pas lesquelles, et c'est
   * l'information qui change la décision. Optionnel, parce que toutes les sources ne savent pas se
   * nommer — celles-là gardent leur ligne de compte, sans liste.
   *
   * ⚠️ **`route` est optionnelle par élément**, pas par groupe : une source peut désigner des entités
   * ouvrables et une autre non. Un élément sans route s'affiche en texte simple — mieux qu'un lien qui
   * retombe sur une liste non filtrée après avoir promis d'arriver sur l'élément.
   */
  elements?: Array<{ libelle: string; route?: string | null }>;
  /** Reste-t-il des éléments au-delà de ceux listés ? (le dialogue l'annonce, il ne le masque pas) */
  tronque?: boolean;
}

@Component({
  selector: "vl-confirmation-dialog",
  standalone: true,
  imports: [
    HeaderDialogComponent,
    BigButtonComponent,
    UsageDetailComponent,
    TranslatePipe,
  ],
  templateUrl: "./confirmation-dialog.component.html",
  styleUrls: ["./confirmation-dialog.component.scss"],
})
export class ConfirmationDialogComponent {
  data = inject(MAT_DIALOG_DATA);
  dialog = inject(MatDialog);
  dialogRef = inject(MatDialogRef<ConfirmationDialogComponent>);

  /**
   * Passé à `vl-usage-detail` : ce qu'il faut faire avant de quitter le dialogue par un lien.
   *
   * ⚠️ **Fermé sur `false`, jamais `true`** : ouvrir la fiche d'un élément n'est pas confirmer sa
   * suppression. Et il DOIT se fermer, sinon son backdrop reste au-dessus de la page d'arrivée.
   */
  fermerAvantNavigation = () => this.dialogRef.close(false);

  // --- Auto-validate config ---
  autoDelayMs = Math.max(
    0,
    parseNumber(
      this.data?.autoDelayMs ??
        (this.data?.autoDelaySec != null ? this.data.autoDelaySec * 1000 : 0),
    ),
  );
  autoAction: "confirm" | "cancel" =
    this.data?.autoAction === "cancel" ? "cancel" : "confirm";
  tickMs = Math.max(50, parseNumber(this.data?.progressTickMs ?? 100)); // rafraîchissement barre/chrono

  // --- Runtime state ---
  countdownSec = Math.ceil(this.autoDelayMs / 1000) || 0;
  progressPct = 0;
  private _autoStartedAt = 0;
  private _autoFired = false;

  constructor() {
    // Comportement existant (disableClose + backdropClick remappé) conservé
    if (this.data?.values) {
      this.dialogRef.disableClose = true;
      this.dialogRef
        .backdropClick()
        .pipe(takeUntilDestroyed())
        .subscribe(() => {
          this.dialogRef.close(this.data.values.backdropClick);
        });
      // `disableClose` coupe aussi Échap : la confirmation ne se fermait plus au clavier.
      // Échap vaut un clic hors de la boîte, la réponse « sans risque » choisie par l'appelant.
      this.dialogRef
        .keydownEvents()
        .pipe(takeUntilDestroyed())
        .subscribe((e) => {
          if (e.key === "Escape") this.dialogRef.close(this.data.values.backdropClick);
        });
    }

    // Compte à rebours + barre de progression (optionnel)
    if (this.autoDelayMs > 0) {
      this._autoStartedAt = Date.now();
      interval(this.tickMs)
        .pipe(takeUntilDestroyed())
        .subscribe(() => this._onTick());
    }
  }

  ngOnInit(): void {}

  private _onTick() {
    if (this._autoFired) return;

    const elapsed = Date.now() - this._autoStartedAt;
    const remaining = Math.max(0, this.autoDelayMs - elapsed);

    this.countdownSec = Math.ceil(remaining / 1000);
    this.progressPct =
      this.autoDelayMs > 0
        ? Math.min(100, (elapsed / this.autoDelayMs) * 100)
        : 0;

    if (remaining <= 0) {
      this._autoFired = true;
      if (this.autoAction === "cancel") {
        this.onCancel();
      } else {
        this.onConfirm();
      }
    }
  }

  /**
   * **La troisième issue** — celle qui ne peut pas échouer.
   *
   * ⚠️ **Pourquoi un troisième bouton et pas deux.** Quand la question est « des saisies vont être
   * perdues », les deux réponses utiles ne sont pas « oui / non » mais « enregistre-les » et « jette-les »
   * — plus le retour en arrière. Avec deux boutons seulement, l'une des deux doit se déguiser en l'autre :
   * c'est ce qui mettait l'action destructrice en vert plein, à droite, là où l'œil va.
   *
   * ⚠️ **Elle reste offerte même quand l'enregistrement échoue** : sans elle, un état que le serveur
   * refuse ferait de la fenêtre une prison (leçon du 08/09/2026).
   */
  onExtra() {
    this._autoFired = true;
    this.dialogRef.close(this.data?.extraValue ?? "EXTRA");
  }

  onCancel() {
    this._autoFired = true;
    if (this.data?.values) {
      this.dialogRef.close(this.data.values.cancel);
    } else {
      this.dialogRef.close(false);
    }
  }

  onConfirm() {
    this._autoFired = true;
    if (this.data?.values) {
      this.dialogRef.close(this.data.values.confirm);
    } else {
      this.dialogRef.close(true);
    }
  }
}
